import { useEffect, useRef, useState } from 'react';

import { useAuth } from './AuthenticationContext.jsx';
import { passkeyCancelled, passkeysSupported } from '../passkeys/webauthn.js';
import HaloLoginDevices from '../components/HaloLoginDevices.jsx';
import LegalFooter from '../legal/LegalFooter.jsx';
import PasswordField from '../components/PasswordField.jsx';
import TextField from '../components/TextField.jsx';

// Error copy mirrors the mobile app's SignIn (app/src/screens/Auth/SignIn.js),
// which is the reference for every string on this screen. The app shows these in
// an alert dialog; here they render inline under the form, which is the web
// convention and what the fields' aria-describedby points at.

// Bad credentials. The app deliberately gives the same message whether the
// account exists or not, so a sign-in attempt can't be used to enumerate emails.
const INCORRECT_CREDENTIALS = 'Incorrect username or password.';

// Everything that isn't one of the recognised cases maps to this single generic
// message, so a raw Cognito exception (which can carry internal detail) is never
// rendered to the user.
const GENERIC_ERROR = 'Please check your account details, or try again later.';

// Cognito raises its exceptions by `name`; the two "next step" cases are re-thrown
// by our auth context as `new Error(code)` (code in `message`), so match on both.
function messageForError(error) {
  const codes = [error && error.name, error && error.message];
  if (
    codes.includes('NotAuthorizedException') ||
    codes.includes('UserNotFoundException')
  ) {
    return INCORRECT_CREDENTIALS;
  }
  if (codes.includes('PasswordResetRequiredException')) {
    // The app sends the user to its in-app code entry here. Web has no code entry
    // (that lives on the app and the /password-code bridge), so point at the reset
    // flow this screen does offer.
    return 'Your password needs to be reset. Use “Forgot Password?” to set a new one.';
  }
  if (codes.includes('UserNotConfirmedException')) {
    // The app navigates to its email-confirmation screen; web can't, so say where
    // to finish.
    return 'Your account isn’t confirmed yet. Open the Halo mobile app to finish confirming it, then log in here.';
  }
  return GENERIC_ERROR;
}

// Passkey sign-in. Cognito needs the account's email to start, so it reads the
// Email field.
const PASSKEY_NEEDS_EMAIL =
  'Enter your email, then choose Sign in with a passkey.';

// The user closed the browser's prompt, or the browser had no passkey for Halo:
// browsers report both the same way on purpose.
const PASSKEY_NOT_USED =
  'No passkey was used. Try again, or log in with your password.';

// One message whether the account doesn't exist, has no such passkey, or can't
// sign in, so passkey sign-in can't be used to probe accounts either.
const PASSKEY_NOT_ACCEPTED =
  'We couldn’t sign you in with a passkey. Check your email, or log in with your password.';

function messageForPasskeyError(error) {
  if (passkeyCancelled(error)) {
    return PASSKEY_NOT_USED;
  }
  const codes = [error && error.name, error && error.message];
  if (
    codes.includes('PasskeyNotAccepted') ||
    codes.includes('NotAuthorizedException') ||
    codes.includes('UserNotFoundException')
  ) {
    return PASSKEY_NOT_ACCEPTED;
  }
  return GENERIC_ERROR;
}

export default function SignIn({ onForgotPassword, autoFocusHeading = false }) {
  const { signIn, signInWithPasskey } = useAuth();
  const [offerPasskey] = useState(passkeysSupported);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const headingRef = useRef(null);

  // Only steal focus when this view is re-shown by a toggle (back from
  // forgot-password), not on the initial gate render.
  useEffect(() => {
    if (autoFocusHeading) {
      headingRef.current?.focus();
    }
  }, [autoFocusHeading]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const session = await signIn(email, password);
      if (session) {
        // A real success flips `authenticated`, which re-gates the app and
        // unmounts this form, so no state update is needed on that path.
        return;
      }
      // signIn resolved but no session materialised (no tokens / a race). The
      // gate won't unmount us, so surface feedback rather than leaving the form
      // stuck disabled with no explanation.
      setError(GENERIC_ERROR);
      setPending(false);
    } catch (ex) {
      setError(messageForError(ex));
      setPending(false);
    }
  };

  const handlePasskey = async () => {
    setError(null);
    if (!email.trim()) {
      setError(PASSKEY_NEEDS_EMAIL);
      return;
    }
    setPending(true);
    try {
      const session = await signInWithPasskey(email.trim());
      if (session) {
        // As with a password sign-in, success re-gates the app and unmounts us.
        return;
      }
      setError(GENERIC_ERROR);
      setPending(false);
    } catch (ex) {
      setError(messageForPasskeyError(ex));
      setPending(false);
    }
  };

  // Layout and copy follow the app's SignIn: the devices illustration, the green
  // "Welcome to Halo," title, placeholder-labelled Email and Password fields with
  // "Forgot Password?" tucked under the password, then the LOGIN button. The app
  // also offers "Create an account" and "Try demo"; both are out of scope for web.
  // Below the form sits the legal footer: the app carries those two links on its
  // sign-up screen, which web does not have (see legal/LegalFooter.jsx).
  // The fields are TextFields: placeholder-labelled like the app, with a hidden
  // <label> for assistive tech (see components/TextField.jsx for the rationale).
  return (
    <main className="app-shell auth-screen">
      <div className="auth-brand">
        <HaloLoginDevices />
      </div>
      <h1
        id="main-content"
        className="auth-screen__title"
        ref={headingRef}
        tabIndex={-1}
      >
        Welcome to Halo,
      </h1>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <TextField
          id="signin-email"
          name="email"
          type="email"
          label="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={pending}
          required
          errorId={error ? 'signin-error' : undefined}
        />

        <PasswordField
          id="signin-password"
          name="password"
          label="Password"
          className="text-field--tight"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={pending}
          required
          errorId={error ? 'signin-error' : undefined}
        />

        {/* type="button" so Enter in a field still submits the form (the submit
            button below is the form's default), and the link can never submit. */}
        <button
          type="button"
          className="link-button auth-form__link"
          onClick={onForgotPassword}
        >
          Forgot Password?
        </button>

        {/* Always present so assistive tech has the live region in the tree
            before an error is set; role=alert announces it when populated. The id
            lets the fields point their aria-describedby at it while it holds an
            error (the fields' errorId above). Collapses to nothing while empty. */}
        <p
          id="signin-error"
          className="form-error"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </p>

        <button type="submit" className="auth-form__submit" disabled={pending}>
          {pending ? 'Logging in…' : 'Login'}
        </button>

        {/* Only where the browser can use passkeys. type="button" so Enter in a
            field still submits the password form. */}
        {offerPasskey ? (
          <button
            type="button"
            className="btn-secondary auth-form__passkey"
            onClick={handlePasskey}
            disabled={pending}
          >
            Sign in with a passkey
          </button>
        ) : null}
      </form>
      <LegalFooter />
    </main>
  );
}
